import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ConfigProvider, Spin, theme } from 'antd';
import zhTW from 'antd/locale/zh_TW';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const Layout = lazy(() => import('./components/Layout'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Historical = lazy(() => import('./pages/Historical'));
const WithdrawalHistory = lazy(() => import('./pages/WithdrawalHistory'));
const AutomatedTrading = lazy(() => import('./pages/AutomatedTrading'));
const ProfitReport = lazy(() => import('./pages/ProfitReport'));
const ProfitCycleEditor = lazy(() => import('./pages/ProfitCycleEditor'));
const StatementUpload = lazy(() => import('./pages/LineReconciliation'));
const Config = lazy(() => import('./pages/Config'));
const Login = lazy(() => import('./pages/Login'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-GN4MSC3CGQ', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
};

const PageFallback = () => (
  <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
    <Spin size="large" />
  </div>
);

const App = () => {
  return (
    <ConfigProvider
      locale={zhTW}
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: '#91b9ce',
          colorSuccess: '#85c9ac',
          colorError: '#FF4D4F',
          colorWarning: '#d8b879',
          borderRadius: 8,
          colorBgBase: '#141719',
          colorBgContainer: '#1c2023',
          colorBorder: '#343b40',
          colorText: '#edf0f2',
          colorTextSecondary: '#a3adb5',
          colorTextPlaceholder: '#8b979f',
          colorErrorText: '#f09b9a',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", "PingFang TC", "Microsoft JhengHei", sans-serif',
        },
      }}
    >
      <ErrorBoundary>
        <AuthProvider>
          <Router>
            <AnalyticsTracker />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* 公開頁面 */}
                <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              
              {/* 受保護的 Dashboard 區域 */}
              <Route path="/dashboard/*" element={
                <ProtectedRoute>
                  <Layout>
                    <ErrorBoundary>
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/historical" element={<Historical />} />
                        <Route path="/withdrawals" element={<WithdrawalHistory />} />
                        <Route path="/automated-trading" element={<AutomatedTrading />} />
                        <Route path="/binance-transfer" element={<AutomatedTrading />} />
                        <Route path="/profit-report" element={<ProfitReport />} />
                        <Route path="/profit-cycles/new" element={<ProfitCycleEditor />} />
                        <Route path="/profit-cycles/:cycleDate/edit" element={<ProfitCycleEditor />} />
                        <Route path="/statements" element={<StatementUpload />} />
                        <Route path="/line-reconciliation" element={<StatementUpload />} />
                        <Route path="/reconciliation" element={<StatementUpload />} />
                        <Route path="/config" element={<Config />} />
                      </Routes>
                    </ErrorBoundary>
                  </Layout>
                </ProtectedRoute>
              } />
              </Routes>
            </Suspense>
          </Router>
        </AuthProvider>
      </ErrorBoundary>
    </ConfigProvider>
  );
};

export default App;
